import { logger } from '@sentry/utils'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { PrescriptionsApi } from 'src/api/client'
import {
  FETCH_USER_PRESCRIPTIONS,
  FETCH_USER_PROGRAMS
} from 'src/constants/queries'
import {
  IPrescription,
  IPrescriptionCreationBody
} from 'src/models/prescription'

/**
 * ----- Queries -----
 */

export const useUserPrescriptions = (userId: string) => {
  const query = useQuery<IPrescription[]>(
    FETCH_USER_PRESCRIPTIONS(userId),
    () => PrescriptionsApi.listUserPrescriptions(userId)
  )

  return { ...query, prescriptions: query.data }
}

/**
 * ----- Mutations -----
 */

export const useCreatePrescription = (userId: string) => {
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (data: IPrescriptionCreationBody) =>
      PrescriptionsApi.createPrescription(userId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FETCH_USER_PRESCRIPTIONS(userId))
        queryClient.invalidateQueries(FETCH_USER_PROGRAMS(userId))
      },
      onError: (err) => {
        logger.error('Error creating a prescription', err)
      }
    }
  )

  return { ...mutation, createPrescription: mutation.mutate }
}

export const useDeletePrescription = (userId: string) => {
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (prescriptionId: string) =>
      PrescriptionsApi.deletePrescription(userId, prescriptionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FETCH_USER_PRESCRIPTIONS(userId))
        queryClient.invalidateQueries(FETCH_USER_PROGRAMS(userId))
      },
      onError: (err) => {
        logger.error('Error deleting this prescription', err)
      }
    }
  )

  return { ...mutation, deletePrescription: mutation.mutate }
}
